import { PAYMENT_TYPES } from '@vend/business-components/dist/constants/payment-types'
import VendNumber, { round, subtract, vn } from 'vend-number'
import { useEffect, useState } from 'react'
import { hasValidCustomerWithLoyalty } from '../../../../functions/customer'
import { useWebRegisterStatusManager } from '../../../../ng-compat/hooks'
import { useAppSelector } from '../../../../state/hooks'
import { selectCurrentSaleAsSale } from '../../../../state/slices/currentSale/currentSaleSlice'

import { hasKlarnaPayments } from '../../../../functions/payment-types/klarna'
import { isABPAddedButNotPaid } from '../../../../functions/payments'
import {
  getOutstandingBalance,
  isAccountBalancePaymentAdded,
  isValidCustomer,
} from '../../../../functions/sale'

import { customerBalancesManager } from '../../../../managers/customer-balances-manager'

/**
 * Hook to determine disabled state of customer payment options: Loyalty & Store Credit
 */
export const usePaymentDisabledState = (amountTendered: string) => {
  const webRegisterStatusManager = useWebRegisterStatusManager()
  const currentSale = useAppSelector(selectCurrentSaleAsSale)

  const [disabledState, setDisabledState] = useState({
    isLoyaltyPaymentDisabled: true,
    isStoreCreditPaymentDisabled: true,
  })

  const checkDisabledState = () =>
    setDisabledState({
      isLoyaltyPaymentDisabled: isLoyaltyPaymentDisabled(),
      isStoreCreditPaymentDisabled: isStoreCreditPaymentDisabled(),
    })

  useEffect(() => {
    checkDisabledState()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amountTendered, currentSale.customer, currentSale.customerBalances])

  /**
   * Update the disabled state of the loyalty button.
   * Loyalty is disabled if
   * - there is no customer
   * - the customer has loyalty disabled (or a loyalty balance of 0)
   * - the amount tendered is $0 or less
   * - the client is offline.
   * - the account balance payment is added but not paid (can't pay ABP by loyalty)
   */
  const isLoyaltyPaymentDisabled = () => {
    if (isNaN(Number(amountTendered))) {
      return true
    }
    const isOnline = webRegisterStatusManager.isOnline()
    const loyaltyAvailable = currentSale.customer
      ? hasValidCustomerWithLoyalty(currentSale.customer)
      : false

    if (isOnline && loyaltyAvailable) {
      const totalLoyaltyPartialPayments = customerBalancesManager.getTotalPartialPayments(
        PAYMENT_TYPES.LOYALTY,
        currentSale
      )
      const loyaltyBalance = currentSale.customer!.loyalty_balance
      const availableLoyaltyBalance = round(
        subtract(loyaltyBalance, totalLoyaltyPartialPayments),
        2
      )

      if (
        vn(availableLoyaltyBalance).lte(0) ||
        vn(amountTendered).lte(0) ||
        isABPAddedButNotPaid(currentSale)
      ) {
        return true
      }
      return false
    }
    return true
  }

  const getAvailableStoreCredit = () => {
    if (!webRegisterStatusManager.isOnline() || !currentSale.customerBalances) {
      return 0
    }
    const storeCredit = currentSale.customerBalances.storeCredit
    const customerStoreCreditBalance = (storeCredit && storeCredit.balance) || 0
    const totalStoreCreditPartialPayments = customerBalancesManager.getTotalPartialPayments(
      PAYMENT_TYPES.STORE_CREDIT,
      currentSale
    )
    const storeCreditAvailable = subtract(
      customerStoreCreditBalance,
      totalStoreCreditPartialPayments
    )
    return round(storeCreditAvailable, 2, VendNumber.ROUNDING_MODES.ROUND_DOWN)
  }

  /**
   * Update the disabled state of the store credit button. Store credit is
   * disabled if
   * - there is no customer
   * - when a positive balance is present but store credit is unavailable
   * - account balance payment is added to sale and store credit is less than the amount
   */
  const isStoreCreditPaymentDisabled = () => {
    const balance = getOutstandingBalance(currentSale)
    const hasNegativeBalance = vn(balance).isNeg()
    const hasValidCustomer = isValidCustomer(currentSale.customer)

    if (hasValidCustomer) {
      const availableStoreCredit = getAvailableStoreCredit()

      // The store credit button is enabled for both negative and positive balances. If we use it with a negative
      // balance the customer will be credited. If you want to spend store credit you have to be on a positive balance
      // sale and have store credit available.
      if (vn(availableStoreCredit).lte(0) && !hasNegativeBalance) {
        return true
      }
      if (
        !hasNegativeBalance &&
        !(
          currentSale.customerBalances &&
          currentSale.customerBalances.storeCredit
        )
      ) {
        return true
      }

      // Disable store credit if Klarna payments have been made on the sale.
      if (
        hasKlarnaPayments({
          payments: currentSale.originalSalePayments,
        })
      ) {
        return true
      }

      if (
        isAccountBalancePaymentAdded(currentSale.payAccountBalance) &&
        vn(currentSale.payAccountBalance!.amount).gt(availableStoreCredit)
      ) {
        return true
      }
      return false
    }
    return true
  }

  return {
    ...disabledState,
  }
}
